/**
 * Which UI Hatch is wearing.
 *
 * Three complete skins ship in one bundle. `classic` is the instrument panel —
 * a phase timeline, a transcript with attached command output, a canvas that
 * resolves into the deployed site. `hatch` is the egg: the same build, told as
 * something hatching, with a feed instead of a transcript. `xano` is the one
 * Xano stands behind at a conference: dark, gridded, phone-first, and set in
 * the product's own type.
 *
 * They are alternatives, not layers. Everything below presentation — the
 * stream, the view model, the readiness gating, the prompt gallery — is shared,
 * so the choice here changes what the build looks like and nothing about what
 * it does.
 *
 * The default is baked in at build time (`VITE_HATCH_UI`, fed from `HATCH_UI`
 * in the root `.env` through the compose build arg). Switching the deployment
 * is one `scripts/deploy.sh --frontend-only`.
 *
 * `?ui=` overrides it per tab. That exists so the skins can be compared against
 * one live droplet without a redeploy, and so local dev can flip without
 * restarting Vite. It is a viewing preference, not a feature flag — nothing is
 * gated on it.
 *
 * `DEFAULT_SKIN` is what an unset or misspelt `HATCH_UI` lands on, and it is
 * `xano` rather than `classic` deliberately. Both README.md and
 * docs/deployment.md point out that a misspelt value is SILENT — the deploy
 * succeeds and serves the fallback — and during a conference that failure mode
 * is a booth showing the wrong product for however long it takes someone to
 * notice. The fallback should be the skin the show wants.
 */

export const SKIN_NAMES = ["classic", "hatch", "xano"] as const;

export type SkinName = (typeof SKIN_NAMES)[number];

export const DEFAULT_SKIN: SkinName = "xano";

function asSkinName(value: string | null | undefined): SkinName | null {
  return (SKIN_NAMES as readonly string[]).includes(value ?? "") ? (value as SkinName) : null;
}

/**
 * The skin this page should mount.
 *
 * Both lookups fail soft. A typo in a deploy environment variable, or a stale
 * `?ui=` link someone shared, should land on a working app rather than a blank
 * page — the cost of being wrong here is the wrong aesthetic, and throwing
 * would make it the wrong aesthetic *and* no site.
 *
 * Resolve this ONCE, not per render. `App` rewrites the URL on start and on
 * restart, and neither rewrite carries the query string forward; re-reading it
 * on every render would swap the skin out from under a running build.
 */
export function resolveSkin(search: string = window.location.search): SkinName {
  const requested = asSkinName(new URLSearchParams(search).get("ui"));
  if (requested) return requested;

  return asSkinName(import.meta.env.VITE_HATCH_UI) ?? DEFAULT_SKIN;
}
